import { useState, useEffect, useRef } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate, Link as RouterLink } from "react-router-dom";

// @mui material components
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";

import Sidenav from "./widgets/Sidenav";

import routes from "./routes";

import { useMaterialUIController, setMiniSidenav } from "./context";
import { ConfirmProvider } from "material-ui-confirm";
import { setGlobalErrorHandler } from "./api/api";
import ErrorModal from "./components/Error";
import Notification from "./components/Notification";
import OnboardingDialog, { isOnboardingComplete } from "./components/OnboardingDialog";
import AppErrorBoundary from "./components/AppErrorBoundary";
import AppBusyIndicator from "./components/AppBusyIndicator";
import VpnOtpDialogHost from "./components/VpnOtpDialogHost";
import UnexpectedErrorHost from "./components/UnexpectedErrorHost";
import { useClientBranding } from "./providers/ClientBrandingProvider";
import "./App.css";
import authService from "./services/authService";
import { SESSION_CHANGED_EVENT } from "./services/sessionAuth";
import ProtectedRoute from "./services/ProtectedRoute";
import Configurator from "./widgets/Configurator";
import {
  canAccessRoute,
  extractRolesFromUser,
  isAdmin,
  isTenantAdmin,
  needsWorkspaceOnboarding,
  postLoginPath,
} from "./utils/authorization";
import { workspaceService } from "./services/workspaceService";
import { useTranslation } from "react-i18next";
import { useApplyThemeMode } from "./hooks/useThemeMode";
import { useStackedTables } from "./hooks/useStackedTables";

export default function App() {
  const [securityChecking, setSecurityChecking] = useState(false);

  const { brandName, brandImageLight, brandImageDark, brandMark } = useClientBranding();
  const [controller, dispatch] = useMaterialUIController();
  const {
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavPinned,
  } = controller;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useApplyThemeMode(dispatch);
  useStackedTables();
  const { t } = useTranslation();
  const [user, setUser] = useState<any>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [setupIncomplete, setSetupIncomplete] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const notificationElement = useRef<any>(null);

  const showSnack = (opts: any) => {
    if (notificationElement.current) {
      notificationElement.current.show(opts);
    }
  };

  // Window event listener for global notification system
  useEffect(() => {
    const handleGlobalNotification = (event: any) => {
      showSnack(event.detail);
    };

    const securityCheck = (event: any) => {
      if (event.detail !== securityChecking) {
        setSecurityChecking(event.detail);
      }
    };

    window.addEventListener("security-check", securityCheck);

    window.addEventListener("showGlobalNotification", handleGlobalNotification);

    return () => {
      window.removeEventListener("security-check", securityCheck);
      window.removeEventListener(
        "showGlobalNotification",
        handleGlobalNotification
      );
    };
  }, [securityChecking]);

  useEffect(() => {
    setGlobalErrorHandler((e: any) => {
      const fallbackContent =
        e?.raw?.message ||
        e?.raw?.error ||
        e?.response?.data?.message ||
        e?.response?.message ||
        (typeof e === "string" ? e : null) ||
        "An unexpected error occurred.";

      const opts: any = {
        color: "error",
        icon: "error",
        title: e?.title || "Unexpected Error",
        reason: e?.reason || "API Request",
        content: e?.content || fallbackContent,
        info: e?.info || "UNKNOWN_ERROR",
      };

      showSnack(opts);
    });

    const loadUserWithRetry = async () => {
      // On the first login redirect, OIDC user may not be immediately available.
      // Retry briefly to avoid empty menu until manual refresh.
      const maxAttempts = 8;
      const retryDelayMs = 350;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const loadedUser: any = await authService.getUser();
        if (loadedUser) {
          setUser(loadedUser);
          setUserRoles(extractRolesFromUser(loadedUser));
          return;
        }

        await new Promise((resolve) => {
          setTimeout(resolve, retryDelayMs);
        });
      }
    };

    loadUserWithRetry().catch((err) => {
      console.error("Failed to load authenticated user:", err);
    });

    const handleSessionChanged = () => {
      authService
        .getUser()
        .then((loadedUser: any) => {
          setUser(loadedUser ?? null);
          setUserRoles(loadedUser ? extractRolesFromUser(loadedUser) : []);
        })
        .catch((err) => console.error("Failed to reload authenticated user:", err));
    };
    window.addEventListener(SESSION_CHANGED_EVENT, handleSessionChanged);

    return () => {
      window.removeEventListener(SESSION_CHANGED_EVENT, handleSessionChanged);
    };
  }, []);

  useEffect(() => {
    if (!user || pathname.startsWith("/s/")) return;
    if (!isOnboardingComplete()) {
      setOnboardingOpen(true);
    }
  }, [user, pathname]);

  // Google / SaaS users without a workspace must finish naming it before seeing an empty shell.
  useEffect(() => {
    if (!user) return;
    if (pathname.startsWith("/onboarding") || pathname.startsWith("/callback") || pathname.startsWith("/logout")) {
      return;
    }
    if (needsWorkspaceOnboarding(userRoles)) {
      navigate("/onboarding/workspace", { replace: true });
    }
  }, [user, userRoles, pathname, navigate]);

  useEffect(() => {
    if (!user) {
      setSetupIncomplete(false);
      return;
    }
    if (isAdmin(userRoles)) {
      setSetupIncomplete(false);
      return;
    }
    if (!isTenantAdmin(userRoles)) return;
    const skipBanner =
      pathname.startsWith("/setup") ||
      pathname.startsWith("/workspace-settings") ||
      pathname.startsWith("/login") ||
      pathname.startsWith("/logout") ||
      pathname.startsWith("/callback") ||
      pathname.startsWith("/invite") ||
      pathname.startsWith("/s/");
    workspaceService
      .setupStatus()
      .then((status) => {
        setSetupIncomplete(!status.setupComplete);
      })
      .catch(() => {
        setSetupIncomplete(false);
      });
    if (skipBanner) {
      // still refresh status above; banner itself is hidden via render condition
    }
  }, [user, userRoles, pathname]);

  // Modal closing function
  const handleCloseModal = () => {
    setModalOpen(false);
    setErrorMessage("");
  };

  /** Unpinned: hover expands / leave collapses (desktop). Pinned: width only changes via sidenav controls. */
  const handleOnMouseEnter = () => {
    if (sidenavPinned) return;
    setMiniSidenav(dispatch, false);
  };

  const handleOnMouseLeave = () => {
    if (sidenavPinned) return;
    setMiniSidenav(dispatch, true);
  };

  const isAuthOnlyPath =
    pathname === "/login" ||
    pathname.startsWith("/login/") ||
    pathname.startsWith("/register") ||
    pathname.startsWith("/logout") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/verify-email") ||
    pathname.startsWith("/invite") ||
    pathname.startsWith("/callback") ||
    pathname.startsWith("/onboarding");

  const isMarketingPath = pathname === "/" || pathname.startsWith("/how-it-works");

  /** No sidenav/configurator on auth pages, marketing pages, public shares, or before a session exists. */
  const hideAppChrome =
    pathname.startsWith("/s/") || isAuthOnlyPath || isMarketingPath || !user;

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.body.classList.toggle("auth-only", hideAppChrome);
    return () => document.body.classList.remove("auth-only");
  }, [hideAppChrome]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  const getRoutes = (allRoutes: any) =>
    allRoutes.map((route: any) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      // Menu-only entries (href or NavLink target without a page component) are skipped here.
      if (route.route && route.component) {
        if (!route?.whiteListed && user && !canAccessRoute(userRoles, route.allowedRoles, route.key)) {
          return (
            <Route
              path={route.route}
              element={<Navigate to={postLoginPath(userRoles)} replace />}
              key={`${route.key}-unauthorized`}
            />
          );
        }

        return route?.whiteListed ? (
          <Route path={route.route} element={route.component} key={route.key} />
        ) : (
          <Route
            path={route.route}
            element={
              <ProtectedRoute user={user}>{route.component}</ProtectedRoute>
            }
            key={route.key}
          />
        );
      }

      return null;
    });

  return (
    <>
      <AppBusyIndicator />
      <VpnOtpDialogHost />
      <Notification ref={notificationElement} />
      <ErrorModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        message={errorMessage}
      />
      <CssBaseline />
      {layout === "dashboard" && !hideAppChrome && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              darkMode ? brandImageLight : brandImageDark
            }
            brandMark={brandMark}
            brandName={brandName}
            routes={routes}
            userRoles={userRoles}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
            size="large"
          />
        </>
      )}

      {setupIncomplete &&
        layout === "dashboard" &&
        !hideAppChrome &&
        !pathname.startsWith("/setup") &&
        !pathname.startsWith("/workspace-settings") && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: { xs: 0, xl: "280px" },
              right: 0,
              zIndex: 1200,
              px: 2,
              pt: 1,
            }}
          >
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  component={RouterLink}
                  to="/workspace-settings"
                >
                  {t("workspace.setup.openSettings")}
                </Button>
              }
            >
              {t("workspace.setup.incompleteBanner")}
            </Alert>
          </Box>
        )}

      <ConfirmProvider
        defaultOptions={{
          title: t("confirm.deleteTitle"),
          confirmationText: t("common.delete"),
          cancellationText: t("common.cancel"),
          confirmationButtonProps: { color: "error", variant: "contained" },
          dialogProps: {
            maxWidth: "xs",
            fullWidth: true,
          },
        }}
      >
        <AppErrorBoundary resetKey={pathname}>
          <Routes>
            {getRoutes(routes)}
            <Route
              path="*"
              element={<Navigate to={user ? postLoginPath(userRoles) : "/login"} replace />}
            />
          </Routes>
        </AppErrorBoundary>
      </ConfirmProvider>
      {/* Configurator Widget */}
      {!hideAppChrome && <Configurator />}
      <OnboardingDialog open={onboardingOpen} onClose={() => setOnboardingOpen(false)} />
    </>
  );
}
